import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, KeyRound, Copy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRole";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

type Status = "loading" | "not_enrolled" | "enrolled";

const TwoFactorCard = ({ autoEnroll = false }: { autoEnroll?: boolean }) => {
  const { user } = useAuth();
  const { isSuperAdmin, isPartnerAdmin } = useRoles();
  const adminLike = isSuperAdmin || isPartnerAdmin;
  const pendingStorageKey = user ? `emrkt-mfa-pending:${user.id}` : null;

  const [status, setStatus] = useState<Status>("loading");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [pendingFactor, setPendingFactor] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [enrolledAt, setEnrolledAt] = useState<string | null>(null);

  const clearPendingEnrollment = () => {
    if (pendingStorageKey) sessionStorage.removeItem(pendingStorageKey);
    setQr(null);
    setSecret(null);
    setPendingFactor(null);
    setCode("");
  };

  const restorePendingEnrollment = async () => {
    if (!pendingStorageKey) return false;
    const raw = sessionStorage.getItem(pendingStorageKey);
    if (!raw) return false;
    try {
      const saved = JSON.parse(raw) as { factorId: string; qr: string; secret: string; createdAt: number };
      if (!saved.factorId || !saved.qr || !saved.secret || Date.now() - saved.createdAt > 15 * 60 * 1000) {
        sessionStorage.removeItem(pendingStorageKey);
        return false;
      }
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const stillPending = factors?.totp?.some((f) => f.id === saved.factorId && f.status !== "verified");
      if (!stillPending) {
        sessionStorage.removeItem(pendingStorageKey);
        return false;
      }
      setEnrollOpen(true);
      setPendingFactor(saved.factorId);
      setQr(saved.qr);
      setSecret(saved.secret);
      return true;
    } catch {
      sessionStorage.removeItem(pendingStorageKey);
      return false;
    }
  };

  const loadStatus = async () => {
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const totp = factors?.totp?.find((f) => f.status === "verified");
    if (totp) {
      setStatus("enrolled");
      setFactorId(totp.id);
      setEnrolledAt(totp.created_at ?? null);
    } else {
      setStatus("not_enrolled");
      setFactorId(null);
    }
    // NOTE: Do NOT auto-cleanup unverified factors here. Right after a
    // successful verify, listFactors can briefly still report the new
    // factor as "unverified" until the session refreshes — deleting it
    // here would wipe the enrollment the user just completed.
    // Cleanup of stale unverified factors happens inside startEnroll().
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const startEnroll = async () => {
    if (enrollOpen || busy) return;
    setBusy(true);
    setRecoveryCodes(null);
    setCode("");

    if (await restorePendingEnrollment()) {
      setBusy(false);
      return;
    }

    // Clear any leftover unverified factors before enrolling, otherwise Supabase
    // returns mfa_factor_name_conflict on the second attempt.
    const { data: existing } = await supabase.auth.mfa.listFactors();
    const verified = existing?.totp?.find((f) => f.status === "verified");
    if (verified) {
      setStatus("enrolled");
      setFactorId(verified.id);
      setEnrolledAt(verified.created_at ?? null);
      setBusy(false);
      toast.info("2FA is already enabled. Remove it before setting up a new authenticator.");
      return;
    }
    for (const f of existing?.totp ?? []) {
      if (f.status !== "verified") {
        await supabase.auth.mfa.unenroll({ factorId: f.id });
      }
    }

    setEnrollOpen(true);

    const friendlyName = `emrkt-${Date.now()}`;
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName,
    });
    setBusy(false);
    if (error || !data) {
      if (error?.message?.includes("Maximum number of verified factors")) {
        await loadStatus();
        toast.info("2FA is already enabled. Remove it before setting up a new authenticator.");
      } else {
        toast.error(error?.message || "Could not start enrollment");
      }
      setEnrollOpen(false);
      return;
    }
    setPendingFactor(data.id);
    setQr(data.totp.qr_code);
    setSecret(data.totp.secret);
    if (pendingStorageKey) {
      sessionStorage.setItem(
        pendingStorageKey,
        JSON.stringify({ factorId: data.id, qr: data.totp.qr_code, secret: data.totp.secret, createdAt: Date.now() }),
      );
    }
  };

  useEffect(() => {
    if (autoEnroll && status === "not_enrolled" && !enrollOpen && !busy) {
      startEnroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEnroll, status]);

  const verifyEnroll = async () => {
    if (!pendingFactor) return;
    setBusy(true);
    const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId: pendingFactor });
    if (chErr || !challenge) {
      setBusy(false);
      toast.error(chErr?.message || "Could not start challenge");
      return;
    }
    const { error } = await supabase.auth.mfa.verify({
      factorId: pendingFactor,
      challengeId: challenge.id,
      code,
    });
    if (error) {
      setBusy(false);
      toast.error(error.message || "Invalid code");
      return;
    }
    // Force the session to pick up the new AAL2 + verified factor before
    // we re-read state. Without this, listFactors can still show the
    // factor as unverified for a beat.
    await supabase.auth.refreshSession();
    // Generate recovery codes.
    const { data, error: rcErr } = await supabase.functions.invoke("mfa-generate-recovery-codes", {});
    setBusy(false);
    if (rcErr || (data && (data as any).error)) {
      toast.error((data as any)?.error || rcErr?.message || "Could not create recovery codes");
    } else {
      setRecoveryCodes((data as { codes: string[] }).codes);
    }
    setEnrollOpen(false);
    clearPendingEnrollment();
    await loadStatus();
    toast.success("Two-factor enabled");
  };

  const regenerateCodes = async () => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("mfa-generate-recovery-codes", {});
    setBusy(false);
    if (error || (data && (data as any).error)) {
      toast.error((data as any)?.error || error?.message || "Could not regenerate codes");
      return;
    }
    setRecoveryCodes((data as { codes: string[] }).codes);
  };

  const removeMfa = async () => {
    if (!factorId) return;
    if (!confirm("Remove two-factor authentication from your account?")) return;
    setBusy(true);
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    setBusy(false);
    if (error) {
      toast.error(error.message || "Could not remove 2FA");
      return;
    }
    toast.success("Two-factor removed");
    setEnrollOpen(false);
    setRecoveryCodes(null);
    await loadStatus();
  };

  const copyCodes = () => {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Recovery codes copied");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4" /> Two-factor authentication
        </CardTitle>
        <CardDescription>
          Add a second step at sign-in using an authenticator app like 1Password, Authy, or Google Authenticator.
          {adminLike && (
            <span className="ml-1 font-medium text-foreground">
              Required for your admin role.
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status === "loading" && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking status…
          </div>
        )}

        {status === "enrolled" && !enrollOpen && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 p-3 text-sm">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Enrolled
              {enrolledAt && (
                <span className="text-muted-foreground">
                  · since {new Date(enrolledAt).toLocaleDateString()}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={regenerateCodes} disabled={busy}>
                <KeyRound className="mr-2 h-4 w-4" />
                Regenerate recovery codes
              </Button>
              <Button variant="destructive" onClick={removeMfa} disabled={busy}>
                Remove 2FA
              </Button>
            </div>
          </div>
        )}

        {status === "not_enrolled" && !enrollOpen && (
          <Button onClick={startEnroll} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enable 2FA
          </Button>
        )}

        {enrollOpen && !recoveryCodes && qr && (
          <div className="space-y-3 rounded-lg border border-border bg-card p-4">
            <p className="text-sm font-medium">Scan this with your authenticator app</p>
            <div className="flex flex-col items-center gap-3 rounded-md bg-white p-3 sm:flex-row sm:items-start">
              <img src={qr} alt="2FA QR code" className="h-40 w-40" />
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Or enter the secret manually:</p>
                <code className="break-all rounded bg-muted px-2 py-1 font-mono text-xs">{secret}</code>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="enroll-code">6-digit code from the app</Label>
              <Input
                id="enroll-code"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={verifyEnroll} disabled={busy || code.length < 6}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Verify and enable
              </Button>
              <Button
                variant="ghost"
                onClick={async () => {
                  if (pendingFactor) await supabase.auth.mfa.unenroll({ factorId: pendingFactor });
                  setEnrollOpen(false);
                  clearPendingEnrollment();
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {recoveryCodes && (
          <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
            <p className="text-sm font-semibold">Save your recovery codes</p>
            <p className="text-xs text-muted-foreground">
              These are shown once. Store them somewhere safe — each one can be used to remove your authenticator if you lose it.
            </p>
            <div className="grid grid-cols-2 gap-2 rounded-md bg-background p-3 font-mono text-sm">
              {recoveryCodes.map((c) => (
                <div key={c} className="rounded border border-border px-2 py-1 text-center">{c}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyCodes}>
                <Copy className="mr-2 h-4 w-4" />
                Copy all
              </Button>
              <Button onClick={() => setRecoveryCodes(null)}>I've saved them</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TwoFactorCard;